import { useEffect, useMemo } from 'react';

import { snakeCase } from 'lodash';

import { useLocation, useNavigate } from 'js/lib/useRouter';
import { isAuthenticatedUser as userIsAuthenticatedUser } from 'js/lib/user';

import localStorageEx from 'bundles/common/utils/localStorageEx';
import DegreesDiscoveryExperiments from 'bundles/epic/clients/DegreesDiscovery';
import type { PremiumHubPageType } from 'bundles/premium-hub/constants';
import { HubPages } from 'bundles/premium-hub/constants';

const LOCALSTORAGE_PREFIX = 'previous_tab_';

type TabName = 'overview' | 'browse';

export const getInfoFromPathname = (pathname: string) => {
  const urlParts = pathname.split('/');
  if (urlParts[urlParts.length - 1] === '') {
    urlParts.pop();
  }
  const inBrowse = urlParts[urlParts.length - 1] === 'browse';
  const slug = inBrowse ? urlParts[urlParts.length - 2] : urlParts[urlParts.length - 1];
  return {
    urlParts,
    inBrowse,
    slug,
  };
};

const getLocalStorageKey = (slug: string) => `${LOCALSTORAGE_PREFIX}${snakeCase(slug)}`;

export const setLastTabLocalStorage = (pathname: string, tab: TabName) => {
  const { slug } = getInfoFromPathname(pathname);
  localStorageEx.setItem<TabName>(getLocalStorageKey(slug), tab, JSON.stringify);
};

/**
 * Automatically redirects the user if not in the experiment and also determines the page slug
 * @param pageType
 * @returns
 */
const useLearnBrowse = (pageType: PremiumHubPageType) => {
  const contemplationVariant = DegreesDiscoveryExperiments.get('contemplationExperience');
  const isInContemplationExperiment = contemplationVariant !== 'control';
  const location = useLocation();
  const navigate = useNavigate();
  const { urlParts, inBrowse, slug } = useMemo(() => getInfoFromPathname(location.pathname), [location.pathname]);

  const onTabbedPage =
    pageType === HubPages.LandingPage || (pageType === HubPages.MainHubPage && isInContemplationExperiment);

  // If a user lands on /browse route and the tab is not available, redirect to the base url
  if (inBrowse && !onTabbedPage) {
    const newURL = urlParts.slice(0, urlParts.length - 1).join('/');
    navigate(`${newURL}`, { replace: true });
  }

  /**
   * when the user first arrives on the core hub page, if they aren't coming from the current page,
   * redirect them to the correct tab
   */
  useEffect(() => {
    if (!isInContemplationExperiment || slug !== 'degrees') return;

    const referrer = document.referrer;
    const urlPartsCopy = [...urlParts];
    if (urlPartsCopy[urlPartsCopy.length - 1] === 'browse') urlPartsCopy.pop();

    /**
     * if the user was referred by the current page, don't redirect them
     * for example, if they clicked on the "view all degrees" button
     */
    if (referrer.includes(urlPartsCopy.join('/'))) return;

    // Don't redirect if the user is not authenticated
    if (!userIsAuthenticatedUser()) return;

    // Users should be redirected to the browse tab if they are in the experiment
    navigate(`${urlParts.join('/')}/browse`, { replace: true });

    // only run useEffect on first render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    slug,
    inBrowse,
  };
};

export default useLearnBrowse;
